import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Plus, X, Upload, Calendar, User, MapPin, 
  AlertCircle, CheckCircle, Clock, Image, Video,
  FileText, Trash2, Eye, Phone, Navigation, UserCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const TaskAssignmentModal = ({ isOpen, onClose, onTaskCreated, editTask = null }) => {
  const [staff, setStaff] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
    due_date: '',
    due_time: '18:00',
    geo_fence_enabled: false,
    geo_fence_lat: '',
    geo_fence_lng: '',
    geo_fence_radius: '100',
    custom_notes: '',
    // Customer Contact Fields
    customer_name: '',
    customer_phone: '',
    customer_alternate_phone: '',
    customer_address: '',
    customer_landmark: '',
    customer_lat: '',
    customer_lng: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchStaff();
      fetchAdmins();
      if (editTask) {
        populateForm(editTask);
      } else {
        resetForm();
      }
    }
  }, [isOpen, editTask]);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('field_staff_token');
      const response = await axios.get(
        `${API_URL}/api/field-staff/admin/staff`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStaff(response.data?.filter(s => s.is_active) || []);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('field_staff_token');
      const response = await axios.get(
        `${API_URL}/api/field-staff/super-admin/admins`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Filter only admins (not super_admin) and active ones
      setAdmins(response.data?.filter(a => a.role === 'admin' && a.is_active !== false) || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      // Non-super-admin users won't have access, that's okay
    }
  };

  const populateForm = (task) => {
    const dueDate = new Date(task.due_date);
    const customerContact = task.customer_contact || {};
    setFormData({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      assigned_to: task.assigned_to || '',
      due_date: dueDate.toISOString().split('T')[0],
      due_time: dueDate.toTimeString().slice(0, 5),
      geo_fence_enabled: !!task.geo_fence,
      geo_fence_lat: task.geo_fence?.latitude || '',
      geo_fence_lng: task.geo_fence?.longitude || '',
      geo_fence_radius: task.geo_fence?.radius_meters || '100',
      custom_notes: task.custom_fields?.notes || '',
      // Customer Contact Fields
      customer_name: customerContact.name || '',
      customer_phone: customerContact.phone || '',
      customer_alternate_phone: customerContact.alternate_phone || '',
      customer_address: customerContact.address || '',
      customer_landmark: customerContact.landmark || '',
      customer_lat: customerContact.latitude || '',
      customer_lng: customerContact.longitude || ''
    });
    setUploadedFiles(task.attachments || []);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      assigned_to: '',
      due_date: new Date().toISOString().split('T')[0],
      due_time: '18:00',
      geo_fence_enabled: false,
      geo_fence_lat: '',
      geo_fence_lng: '',
      geo_fence_radius: '100',
      custom_notes: '',
      // Customer Contact Fields
      customer_name: '',
      customer_phone: '',
      customer_alternate_phone: '',
      customer_address: '',
      customer_landmark: '',
      customer_lat: '',
      customer_lng: ''
    });
    setUploadedFiles([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('field_staff_token');
      const formDataObj = new FormData();
      files.forEach(file => formDataObj.append('files', file));

      const response = await axios.post(
        `${API_URL}/api/field-staff/admin/upload-files`,
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data?.files) {
        setUploadedFiles(prev => [...prev, ...response.data.files]);
        toast.success(`${files.length} file(s) uploaded successfully`);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.assigned_to || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('field_staff_token');
      
      const dueDateTime = new Date(`${formData.due_date}T${formData.due_time}`);
      
      const taskData = {
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        assigned_to: formData.assigned_to,
        due_date: dueDateTime.toISOString(),
        attachments: uploadedFiles.map(f => f.id),
        custom_fields: {
          notes: formData.custom_notes
        }
      };

      // Add geo-fence if enabled
      if (formData.geo_fence_enabled && formData.geo_fence_lat && formData.geo_fence_lng) {
        taskData.geo_fence = {
          latitude: parseFloat(formData.geo_fence_lat),
          longitude: parseFloat(formData.geo_fence_lng),
          radius_meters: parseFloat(formData.geo_fence_radius)
        };
      }

      // Add customer contact if provided
      if (formData.customer_name || formData.customer_phone || formData.customer_address) {
        taskData.customer_contact = {
          name: formData.customer_name,
          phone: formData.customer_phone,
          alternate_phone: formData.customer_alternate_phone || null,
          address: formData.customer_address,
          landmark: formData.customer_landmark || null,
          latitude: formData.customer_lat ? parseFloat(formData.customer_lat) : null,
          longitude: formData.customer_lng ? parseFloat(formData.customer_lng) : null
        };
      }

      if (editTask) {
        await axios.put(
          `${API_URL}/api/field-staff/admin/tasks/${editTask.id}`,
          taskData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/field-staff/admin/tasks`,
          taskData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Task created successfully');
      }

      if (onTaskCreated) onTaskCreated();
      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(error.response?.data?.detail || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Plus className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{editTask ? 'Edit Task' : 'Create New Task'}</h2>
                <p className="text-indigo-100 text-sm">Assign work to staff or admin</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {/* Title */}
            <div>
              <Label className="text-sm font-medium">Task Title *</Label>
              <Input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter task title"
                className="mt-1"
                required
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe the task in detail..."
                className="mt-1 min-h-[80px]"
              />
            </div>

            {/* Priority & Assigned To */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Priority *</Label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="mt-1 w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🟠 High</option>
                  <option value="urgent">🔴 Urgent</option>
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium">Assign To *</Label>
                <select
                  name="assigned_to"
                  value={formData.assigned_to}
                  onChange={handleInputChange}
                  className="mt-1 w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select assignee</option>
                  {admins.length > 0 && (
                    <optgroup label="👔 Admins">
                      {admins.map(admin => (
                        <option key={admin.id} value={admin.id}>
                          {admin.name} - Admin
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {staff.length > 0 && (
                    <optgroup label="👷 Staff Members">
                      {staff.map(member => (
                        <option key={member.id} value={member.id}>
                          {member.name} - {member.position || 'Staff'}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>

            {/* Due Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Due Date *</Label>
                <Input
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Due Time</Label>
                <Input
                  type="time"
                  name="due_time"
                  value={formData.due_time}
                  onChange={handleInputChange}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Customer/LC Contact Details */}
            <div className="border rounded-lg p-4 bg-blue-50/50">
              <div className="flex items-center gap-2 mb-3">
                <UserCircle className="w-5 h-5 text-blue-600" />
                <Label className="text-sm font-semibold text-blue-800">Customer / LC Contact Details</Label>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-gray-600">Contact Name</Label>
                  <Input
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    placeholder="Customer name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Phone Number</Label>
                  <Input
                    name="customer_phone"
                    value={formData.customer_phone}
                    onChange={handleInputChange}
                    placeholder="Primary phone"
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="mt-3">
                <Label className="text-xs text-gray-600">Alternate Phone (Optional)</Label>
                <Input
                  name="customer_alternate_phone"
                  value={formData.customer_alternate_phone}
                  onChange={handleInputChange}
                  placeholder="Alternate phone number"
                  className="mt-1"
                />
              </div>
              
              <div className="mt-3">
                <Label className="text-xs text-gray-600">Address</Label>
                <Textarea
                  name="customer_address"
                  value={formData.customer_address}
                  onChange={handleInputChange}
                  placeholder="Full address"
                  className="mt-1 min-h-[60px]"
                />
              </div>
              
              <div className="mt-3">
                <Label className="text-xs text-gray-600">Landmark (Optional)</Label>
                <Input
                  name="customer_landmark"
                  value={formData.customer_landmark}
                  onChange={handleInputChange}
                  placeholder="Near landmark for easy navigation"
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-xs text-gray-600 flex items-center gap-1">
                    <Navigation className="w-3 h-3" /> Latitude
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    name="customer_lat"
                    value={formData.customer_lat}
                    onChange={handleInputChange}
                    placeholder="28.6139"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-600 flex items-center gap-1">
                    <Navigation className="w-3 h-3" /> Longitude
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    name="customer_lng"
                    value={formData.customer_lng}
                    onChange={handleInputChange}
                    placeholder="77.2090"
                    className="mt-1"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Add coordinates to enable Google Maps navigation for staff
              </p>
            </div>

            {/* Geo-fence */}
            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="geo_fence_enabled"
                  name="geo_fence_enabled"
                  checked={formData.geo_fence_enabled}
                  onChange={handleInputChange}
                  className="rounded"
                />
                <Label htmlFor="geo_fence_enabled" className="text-sm font-medium cursor-pointer">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Enable Location Verification (Geo-fence)
                </Label>
              </div>
              
              {formData.geo_fence_enabled && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div>
                    <Label className="text-xs">Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      name="geo_fence_lat"
                      value={formData.geo_fence_lat}
                      onChange={handleInputChange}
                      placeholder="20.5937"
                      className="mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      name="geo_fence_lng"
                      value={formData.geo_fence_lng}
                      onChange={handleInputChange}
                      placeholder="78.9629"
                      className="mt-1 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Radius (meters)</Label>
                    <Input
                      type="number"
                      name="geo_fence_radius"
                      value={formData.geo_fence_radius}
                      onChange={handleInputChange}
                      placeholder="100"
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* File Upload */}
            <div>
              <Label className="text-sm font-medium">Attachments (Photos, Videos, Documents)</Label>
              <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center bg-gray-50 hover:bg-gray-100 transition-colors">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="mb-2"
                >
                  {uploading ? (
                    <>Uploading...</>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Files
                    </>
                  )}
                </Button>
                <p className="text-xs text-gray-500">
                  Supported: Images, Videos, PDF, DOC (Max 10MB each)
                </p>
              </div>

              {/* Uploaded Files List */}
              {uploadedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {uploadedFiles.map((file, idx) => (
                    <div key={file.id || idx} className="flex items-center justify-between bg-gray-100 rounded-lg p-2">
                      <div className="flex items-center gap-2">
                        {file.content_type?.startsWith('image') ? (
                          <Image className="w-4 h-4 text-blue-500" />
                        ) : file.content_type?.startsWith('video') ? (
                          <Video className="w-4 h-4 text-purple-500" />
                        ) : (
                          <FileText className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="text-sm truncate max-w-[200px]">{file.filename || file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(file.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div>
              <Label className="text-sm font-medium">Additional Notes</Label>
              <Textarea
                name="custom_notes"
                value={formData.custom_notes}
                onChange={handleInputChange}
                placeholder="Any special instructions or notes..."
                className="mt-1"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? 'Saving...' : editTask ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskAssignmentModal;
